/**
 * Shared hero banner for Student Loan Contribution pages.
 *
 * Renders a gradient hero with greeting + a responsive metric grid. The
 * card set varies by plan-limit shape (annual / lifetime / both / none):
 *
 *   variant 1 — annual limit  : annual-usage, lifetime, last?, next
 *   variant 2 — lifetime only : lifetime-usage, last?, next
 *   variant 3 — no limit      : year, lifetime, last?, next
 *   variant 4 — both limits   : annual-usage, lifetime-usage, last?, next
 *
 * The 4 layouts share 6 card kinds. We list which kinds belong to each
 * variant, then let `CardGrid` (container-query auto-fit) drive how many
 * columns to render. No per-variant grid markup, no duplicated card JSX.
 */
import Component from '@glimmer/component';
import { on } from '@ember/modifier';
import { concat } from '@ember/helper';
import { t } from 'ember-intl';
import style from 'ember-style-modifier/modifiers/style';
import { or } from 'tio-ui/utilities';
import type { TOC } from '@ember/component/template-only';
import Heading from 'tio-ui/components/heading';
import CardGrid from 'tio-ui/components/card-grid';
import { formatCurrency } from 'tio-ui/format';
import {
  PhCalendarBlank,
  PhCheckCircle,
  PhPaperPlaneTilt,
  PhGift,
  PhMoneyWavy,
  PhProhibit,
  PhWarningCircle,
} from 'ember-phosphor-icons';

export interface ContributionHeroSignature {
  Args: {
    greeting: string;
    firstName: string;
    variant: 1 | 2 | 3 | 4;
    blocked?: boolean;
    annualUsage?: number;
    annualLimit?: number;
    annualProgressPercent?: number;
    lifetimeUsage?: number;
    lifetimeLimit?: number;
    lifetimeProgressPercent?: number;
    lifetimeContribution?: number;
    lifetimeAsOfDate?: string;
    currentYear?: number;
    yearContribution?: number;
    paymentCount?: number;
    lastPaymentAmount?: number;
    lastPaymentDate?: string;
    nextContribution: number;
    nextContributionDate: string;
    /** Loan-standing verification is blocking the next contribution (strikethrough + red note). */
    nextContributionRequiresVerification?: boolean;
    onViewBenefit?: () => void;
  };
}

// ---------------------------------------------------------------------------
// Local TOC cards — each renders the on-hero stat-card surface with a
// label/value top and a status footer at the bottom. The cards apply
// `flex flex-col justify-between` themselves so the footer is pushed down;
// `.app-stat-card--on-hero` contributes only surface styling (translucent
// background, radius, padding). Cards with progress bars include the bar
// between the value and the footer.
// ---------------------------------------------------------------------------
interface UsageCardArgs {
  label: string;
  usage: number;
  limit: number;
  progressPercent: number;
  limitReached: boolean;
  limitLabel: string;
}

const UsageCard: TOC<{ Args: UsageCardArgs }> = <template>
  <div
    class="app-stat-card app-stat-card--on-hero tio-contribution-card"
    role="group"
    aria-label={{@label}}
  >
    <div class="tio-contribution-card__group">
      <div class="app-stat-card-label tio-contribution-card__label">{{@label}}</div>
      <div class="app-stat-card-value tio-contribution-card__value">{{formatCurrency @usage}}</div>
    </div>
    <div class="tio-contribution-card__progress">
      <div
        class="tio-contribution-card__track"
        role="progressbar"
        aria-valuenow={{@progressPercent}}
        aria-valuemin={{0}}
        aria-valuemax={{100}}
        aria-label={{@label}}
      >
        <div class="tio-contribution-card__bar" {{style width=(concat @progressPercent "%")}}></div>
      </div>
      <div class="tio-contribution-card__footer">
        {{#if @limitReached}}
          <span class="tio-contribution-card__footer-text">
            {{t "slr.reimagined.limit_reached"}}
          </span>
          <PhProhibit @size={{16}} />
        {{else}}
          <span class="tio-contribution-card__footer-text">
            {{@limitLabel}}
            {{formatCurrency @limit}}
          </span>
          <PhCalendarBlank @size={{16}} />
        {{/if}}
      </div>
    </div>
  </div>
</template>;

interface FooterCardArgs {
  label: string;
  value: number;
  footerLabel: string;
  icon: typeof PhCheckCircle;
}

const FooterCard: TOC<{ Args: FooterCardArgs }> = <template>
  <div
    class="app-stat-card app-stat-card--on-hero tio-contribution-card"
    role="group"
    aria-label={{@label}}
  >
    <div class="tio-contribution-card__group">
      <div class="app-stat-card-label tio-contribution-card__label">{{@label}}</div>
      <div class="app-stat-card-value tio-contribution-card__value">{{formatCurrency @value}}</div>
    </div>
    <div class="tio-contribution-card__footer">
      <span class="tio-contribution-card__footer-text">{{@footerLabel}}</span>
      {{#let @icon as |Icon|}}
        <Icon @size={{16}} />
      {{/let}}
    </div>
  </div>
</template>;

interface NextContributionCardArgs {
  amount: number;
  date: string;
  blocked: boolean;
  requiresVerification?: boolean;
}

const NextContributionCard: TOC<{ Args: NextContributionCardArgs }> = <template>
  <div
    class="app-stat-card app-stat-card--on-hero tio-contribution-card"
    role="group"
    aria-label={{t "slr.reimagined.next_contribution"}}
  >
    <div class="tio-contribution-card__group">
      <div class="app-stat-card-label tio-contribution-card__label">
        {{t "slr.reimagined.next_contribution"}}
      </div>
      <div
        class="app-stat-card-value tio-contribution-card__value
          {{if
            @requiresVerification
            'tio-contribution-card__value--requires-verification'
            (if
              @blocked
              'tio-contribution-card__value--blocked'
              'tio-contribution-card__value--default'
            )
          }}"
        data-test-next-contribution-amount
      >
        {{formatCurrency @amount}}
      </div>
    </div>
    <div
      class="tio-contribution-card__footer
        {{if (or @blocked @requiresVerification) 'tio-contribution-card__footer--blocked'}}"
    >
      {{#if @requiresVerification}}
        <span
          class="tio-contribution-card__footer-text tio-contribution-card__footer-text--requires-verification"
          data-test-next-contribution-requires-verification
        >
          {{t "slr.loan_standing.attention.requires_verification"}}
        </span>
        <PhWarningCircle
          @size={{16}}
          class="tio-contribution-card__footer-text--requires-verification"
        />
      {{else if @blocked}}
        <span class="tio-contribution-card__footer-text">
          {{t "slr.reimagined.unable_to_process"}}
        </span>
        <PhWarningCircle @size={{16}} />
      {{else}}
        <span class="tio-contribution-card__footer-text">
          {{t "slr.reimagined.expected_date" date=@date}}
        </span>
        <PhPaperPlaneTilt @size={{16}} />
      {{/if}}
    </div>
  </div>
</template>;

export default class ContributionHero extends Component<ContributionHeroSignature> {
  handleViewBenefit = () => {
    this.args.onViewBenefit?.();
  };

  get isAnnualLimitReached() {
    return (this.args.annualProgressPercent ?? 0) >= 100;
  }

  get isLifetimeLimitReached() {
    return (this.args.lifetimeProgressPercent ?? 0) >= 100;
  }

  /**
   * Per-card getters — each variant decides which subset to render, then
   * template order decides the layout. Replaces the 4 hand-rolled variant
   * blocks (each ~80 lines of duplicated inline card markup) with one
   * declarative path per card kind.
   */
  get showAnnualUsage() {
    return this.args.variant === 1 || this.args.variant === 4;
  }

  get showLifetimeUsage() {
    return this.args.variant === 2 || this.args.variant === 4;
  }

  get showLifetimeContribution() {
    return this.args.variant === 1 || this.args.variant === 3;
  }

  get showYearContribution() {
    return this.args.variant === 3;
  }

  get showLastPayment() {
    return !!this.args.lastPaymentDate;
  }

  <template>
    <div class="tio-contribution-hero" data-test-slr-contribution-hero>
      {{! Decorative circles }}
      <div class="tio-contribution-hero__circle tio-contribution-hero__circle--1 hero-float"></div>
      <div
        class="tio-contribution-hero__circle tio-contribution-hero__circle--2 hero-float"
        {{style animation-delay="-18s"}}
      ></div>
      <div
        class="tio-contribution-hero__circle tio-contribution-hero__circle--3 hero-float"
        {{style animation-delay="-35s"}}
      ></div>

      {{! Header }}
      <div class="tio-contribution-hero__header">
        <div class="tio-contribution-hero__header-left">
          <div class="tio-contribution-hero__greeting">
            <PhMoneyWavy @size={{20}} />
            <span class="tio-contribution-hero__greeting-name">{{@greeting}}, {{@firstName}}</span>
          </div>
          <Heading @level={{2}} class="tio-contribution-hero__heading leading-tight">
            {{t "slr.reimagined.your_student_loan_contributions"}}
          </Heading>
        </div>
        <button
          type="button"
          class="tio-contribution-hero__benefit-link"
          {{on "click" this.handleViewBenefit}}
        >
          {{t "slr.reimagined.benefit_details_link"}}
        </button>
      </div>

      {{! Metric grid — single CardGrid drives column count via container query.
          Variant decides which cards to render; CardGrid decides layout. }}
      <CardGrid @density="narrow">
        {{#if this.showAnnualUsage}}
          <UsageCard
            @label={{t "slr.reimagined.annual_usage"}}
            @usage={{or @annualUsage 0}}
            @limit={{or @annualLimit 0}}
            @progressPercent={{or @annualProgressPercent 0}}
            @limitReached={{this.isAnnualLimitReached}}
            @limitLabel={{t "slr.reimagined.annual_limit"}}
          />
        {{/if}}
        {{#if this.showLifetimeUsage}}
          <UsageCard
            @label={{t "slr.reimagined.lifetime_usage"}}
            @usage={{or @lifetimeUsage 0}}
            @limit={{or @lifetimeLimit 0}}
            @progressPercent={{or @lifetimeProgressPercent 0}}
            @limitReached={{this.isLifetimeLimitReached}}
            @limitLabel={{t "slr.reimagined.lifetime_limit"}}
          />
        {{/if}}
        {{#if this.showLifetimeContribution}}
          <FooterCard
            @label={{t "slr.reimagined.lifetime_contribution"}}
            @value={{or @lifetimeContribution 0}}
            @footerLabel={{if
              @lifetimeAsOfDate
              (t "slr.reimagined.as_of_date" date=@lifetimeAsOfDate)
              (t "slr.reimagined.over_lifetime")
            }}
            @icon={{PhGift}}
          />
        {{/if}}
        {{#if this.showYearContribution}}
          <FooterCard
            @label={{t "slr.reimagined.year_contribution" year=@currentYear}}
            @value={{or @yearContribution 0}}
            @footerLabel={{concat
              (or @paymentCount 0)
              " "
              (t "slr.reimagined.contribution_count" count=(or @paymentCount 0))
            }}
            @icon={{PhMoneyWavy}}
          />
        {{/if}}
        {{#if this.showLastPayment}}
          <FooterCard
            @label={{t "slr.reimagined.last_contribution"}}
            @value={{or @lastPaymentAmount 0}}
            @footerLabel={{t "slr.reimagined.sent_date" date=@lastPaymentDate}}
            @icon={{PhCheckCircle}}
          />
        {{/if}}
        <NextContributionCard
          @amount={{@nextContribution}}
          @date={{@nextContributionDate}}
          @blocked={{if @blocked true false}}
          @requiresVerification={{@nextContributionRequiresVerification}}
        />
      </CardGrid>
    </div>
  </template>
}
